import type { GameLike } from './GameCard.js';

export type LotteryArt =
  | { kind: 'flag'; cc: string }
  | { kind: 'yi-ki'; round: number }
  | { kind: 'stock'; cc: string | null }
  | { kind: 'special'; iconName: 'Sparkles' | 'Crown' }
  | { kind: 'fallback' };

export function isLotteryGame(game: GameLike): boolean {
  if (game.category === 'lottery') return true;
  return /^หวย/.test(game.name);
}

const BRAND_CC: Record<string, string> = {
  dowjones: 'us',
  hangseng: 'hk',
  nikkei: 'jp',
  shanghai: 'cn',
  hanoi: 'vn',
  saigon: 'vn',
};

function ccFromCode(code: string): string | null {
  // Strip the kind prefix (main_/stock_/set_/government_).
  const stripped = code.replace(/^(main|stock|set|government)_/i, '');
  // First token before the next underscore.
  const head = stripped.split('_')[0]?.toLowerCase() ?? '';
  if (!head) return null;
  if (/^[a-z]{2}$/.test(head)) return head;
  if (BRAND_CC[head]) return BRAND_CC[head];
  return null;
}

function ccFromName(name: string): string | null {
  const m: Array<[RegExp, string]> = [
    [/หุ้น.*ดาวโจนส์|ดาวโจนส์/, 'us'],
    [/หุ้น.*นิเคอิ|หุ้น.*นิเกอิ|นิเคอิ|นิเกอิ/, 'jp'],
    [/หุ้น.*ฮั่งเส็ง|ฮั่งเส็ง/, 'hk'],
    [/หุ้น.*(แซ่งไฮ้|เซี่ยงไฮ้|Shanghai)/i, 'cn'],
    [/ลาว/, 'la'],
    [/เวียดนาม|ฮานอย|ไซ่ง่อน/, 'vn'],
    [/มาเลย์|มาเลเซีย/, 'my'],
    [/ฮ่องกง/, 'hk'],
    [/สิงคโปร์/, 'sg'],
    [/จีน/, 'cn'],
    [/ไต้หวัน/, 'tw'],
    [/เกาหลี/, 'kr'],
    [/ญี่ปุ่น/, 'jp'],
    [/อังกฤษ|ลอนดอน/, 'gb'],
    [/เยอรมัน/, 'de'],
    [/อเมริกา/, 'us'],
    [/รัสเซีย/, 'ru'],
    [/อินเดีย/, 'in'],
    [/อียิปต์/, 'eg'],
    [/กัมพูชา/, 'kh'],
    [/ฟิลิปปินส์/, 'ph'],
    [/นิวซีแลนด์/, 'nz'],
    [/รัฐบาลไทย|หวยไทย|สลากกินแบ่ง/, 'th'],
    [/หุ้นไทย|หวยไทย/, 'th'],
  ];
  for (const [re, cc] of m) {
    if (re.test(name)) return cc;
  }
  return null;
}

function extractRound(name: string, code: string | undefined): number {
  const fromCode = code ? /^yeekee_(\d+)$/i.exec(code) : null;
  if (fromCode) return Number(fromCode[1]);
  const fromName = /(\d+)/.exec(name);
  return fromName ? Number(fromName[1]) : 0;
}

export function resolveLotteryArt(game: GameLike): LotteryArt {
  const name = game.name;
  const code = game.code ?? '';
  const lt = (game.lotteryType ?? '').toUpperCase();

  // Yi-Ki: by code or lotteryType.
  if (/^yeekee_/i.test(code) || lt === 'YEEKEE') {
    return { kind: 'yi-ki', round: extractRound(name, code) };
  }

  // Stock: by code prefix or lotteryType.
  if (/^stock_/i.test(code) || lt === 'STOCK') {
    const cc = ccFromCode(code) ?? ccFromName(name);
    return { kind: 'stock', cc };
  }

  // Country-flagged kinds (MAIN, SET, GOVERNMENT) — also accept name-only inputs.
  if (/^(main|set|government)_/i.test(code) || lt === 'MAIN' || lt === 'SET' || lt === 'GOVERNMENT') {
    const cc = ccFromCode(code) ?? ccFromName(name);
    if (cc) return { kind: 'flag', cc };
  }

  // Name-only fallbacks (no code, no recognised lotteryType).
  if (/ยี่กี/.test(name)) {
    return { kind: 'yi-ki', round: extractRound(name, undefined) };
  }
  if (/หุ้น/.test(name)) {
    return { kind: 'stock', cc: ccFromName(name) };
  }
  const cc = ccFromName(name);
  if (cc) return { kind: 'flag', cc };

  return { kind: 'fallback' };
}
